import React, { useState, useEffect, useContext, useCallback } from 'react';
import { catalogSVC as Product } from '@com/core/common/services/api';
// import { StoreContext } from '@com/core/common/contexts/storeContext';
import { AppContext } from '@com/core/common/contexts/appContext';
import { useRouter } from 'next/router';
import { TCatalogResponse, THit } from '@com/core/common/types/catalog';
import { reportEvent } from '@com/core/common/utils/sentry';
import { getRecommendationSkus } from '../utils/recommendations';
// import Analytics from '@com/core/common/utils/analytics';

type TProps = {
    catalogData: TCatalogResponse;
    children: JSX.Element | Element;
};

type TRecommendationContext = {
    recommendedSkus: string[];
    recommendedItems: THit[];
};

const initContext: TRecommendationContext = {
    recommendedSkus: [],
    recommendedItems: [],
};

const recEventPageTypeMap = {
    static: 'page_static',
    detail: 'page_detail',
};

export const RecommendationContext = React.createContext<TRecommendationContext>(initContext);

export const RecommendationContextProvider = ({ catalogData, children }: TProps): React.ReactElement => {
    const router = useRouter();
    // const { storeCode } = useContext(StoreContext);
    const {
        locale,
        webConfig: { recommendation },
    } = useContext(AppContext);

    const [recommendedSkus, setRecommendedSkus] = useState<string[]>(initContext.recommendedSkus);
    const [recommendedItems, setRecommendedItems] = useState<THit[]>(initContext.recommendedItems);

    const getRecommendedItems = async (): Promise<void> => {
        try {
            const catalogItemIds = await getRecommendationSkus({
                event: router.pathname === 'cart' ? 'page_cart' : recEventPageTypeMap[catalogData.type],
                catalogData,
                locale,
            });

            setRecommendedSkus(catalogItemIds);

            // NOTE - we do not want to load recommended products data on static pages.
            // It will be taken care of by the DynamicWidget component
            if (catalogData.type !== 'static') {
                if (catalogItemIds.length) {
                    const recommendations = await Product(locale.apiLocale).getProductDetailsForSkus(
                        catalogItemIds.join(','),
                    );
                    setRecommendedItems(recommendations.data.hits);
                } else {
                    setRecommendedItems([]);
                }
            }
        } catch (e) {
            reportEvent(
                'Could not fetch recommendation items',
                'RecommendationContextProvider.getRecommendationSkus()',
            );
        }
    };

    useEffect(() => {
        if (
            recommendation.page_detail ||
            recommendation.add_to_cart ||
            recommendation.page_home ||
            recommendation.page_cart
        ) {
            getRecommendedItems();
        }

        // Analytics.trackPage(locale, {
        //     event: 'page_detail',
        //     eventAction: product.sku,
        //     eventLabel: product.product_title,
        //     canonicalUrl: catalogData.canonical_url,
        //     store: storeCode || null,
        // });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const recommendationContextValue = {
        recommendedSkus,
        recommendedItems,
    };

    return (
        <RecommendationContext.Provider value={recommendationContextValue}>{children}</RecommendationContext.Provider>
    );
};
